import { useReactFlow } from 'reactflow';
import { Box, Editable, EditableInput, EditablePreview, HStack } from '@chakra-ui/react';
import { PointerEventHandler } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { OPTION_PILL_MAX_W } from '@duke-hq/ui/components/Conversation/ConversationOptionButton';
import { GripVertical } from 'lucide-react';
import { BlockOption } from '../flowEditorBlockNodeTypes';
import { flowEditorState } from '../../flowEditorState';
import { BlockConnectMenu } from './BlockConnectMenu';
import { ItemActionsMenu } from './ItemActionsMenu';
import { testIds as blockNodeTestIds } from '../FlowEditorBlockNode/testIds';

export type OptionItemProps = {
  option: BlockOption;
  nodeId: string;
  removeOption: (optionId: string) => void;
  isConnectable: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  /**
   * True while the parent node is being dragged. Makes the Reorder.Item layout
   * transition instant so the pill tracks the node rigidly rather than
   * springing to catch up with it.
   */
  dragging: boolean;
};

export function BlockOptionWithOutgoingMenu({
  nodeId,
  removeOption,
  isConnectable,
  option,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  dragging,
}: OptionItemProps) {
  const { setNodes } = useReactFlow();
  const dragControls = useDragControls();

  const handleOnSubmit = (val: string) => {
    // Chakra's Editable submits on blur, so clicking into an option label and
    // leaving without changing it fires this with the unchanged title. Bail on
    // that no-op so a bare focus/blur doesn't rebuild node.data and mark the
    // flow dirty.
    if (val === option.title) {
      return;
    }

    // Mark dirty so the rename autosaves and the history recorder captures it.
    // Return a new nodes array (and a new node object for the renamed node)
    // rather than mutating in place, so React Flow reliably reconciles.
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                options: node.data.options.map((opt: BlockOption) =>
                  opt.id === option.id ? { ...opt, title: val } : opt,
                ),
                outgoers: node.data.outgoers,
              },
            }
          : node,
      ),
    );

    flowEditorState.markDirty();
  };

  // Drag starts only from the grip (dragListener is off on the item), so the
  // pill stays selectable and click-to-edit.
  const startDrag: PointerEventHandler<HTMLDivElement> = (e) => {
    dragControls.start(e);
  };

  return (
    <Reorder.Item
      value={option}
      // Animate position only. `layout` must stay on for the group to keep
      // measuring each pill (that measurement is what makes reordering work),
      // so we can't switch it off while the node drags. Instead the transition
      // below goes instant during a node drag.
      layout="position"
      // No layout animation while the node is dragged across the canvas:
      // otherwise each pill springs to catch up with the moving node, trailing
      // outside the node frame (the rubber-band effect). A normal ease only
      // when actually reordering pills.
      transition={{ layout: dragging ? { duration: 0 } : { duration: 0.2, ease: 'easeOut' } }}
      dragListener={false}
      dragControls={dragControls}
      style={{ width: '100%', listStyleType: 'none' }}
    >
      <BlockConnectMenu isConnectable={isConnectable} nodeId={nodeId} option={option}>
        {/* Right-aligned like the learner's choice, with a right inset wide
            enough to hold the actions menu trigger (positioned just past the
            pill: ml=1 gap + the xs icon button) so it sits clear of the node
            frame rather than overlapping its edge. The grip and menu are out of
            the layout flow, so revealing them on hover never reflows the
            option — the same hover affordance as a message bubble. */}
        <HStack role="group" alignItems="center" justifyContent="flex-end" width="100%" pr={9}>
          <Box
            // Matches ConversationOptionButton (the student-facing pill): hug
            // the label, wrap as a paragraph, share OPTION_PILL_MAX_W with the
            // static pill so neither reflows on selection.
            position="relative"
            border="1px"
            borderColor="brand.600"
            borderRadius="2xl"
            py="1"
            px="3"
            w="fit-content"
            maxW={OPTION_PILL_MAX_W}
            color="brand.600"
            fontWeight="semibold"
            whiteSpace="normal"
            wordBreak="break-word"
            _hover={{ backgroundColor: 'brand.50' }}
          >
            <Editable defaultValue={option.title} onSubmit={handleOnSubmit}>
              <EditablePreview height="auto" />
              <EditableInput
                _focusVisible={{ outline: 'none' }}
                // Stay clickable while editing even if the label is cleared,
                // without pinning the preview width (which would stop the pill
                // hugging its content).
                minW="6rem"
              />
            </Editable>
            {/* Reorder grip in the left margin, the only drag target. */}
            <Box
              position="absolute"
              right="100%"
              top="50%"
              transform="translateY(-50%)"
              mr="2"
              onPointerDown={startDrag}
              cursor="grab"
              opacity={0}
              _groupHover={{ opacity: 1 }}
              aria-label="Drag to reorder option"
              role="img"
              style={{ touchAction: 'none' }}
            >
              <GripVertical size={iconSize.lg} color="grey" />
            </Box>
            {/* Actions menu in the right margin, the same Move up/down/Delete
                menu the message bubbles use. */}
            <Box position="absolute" left="100%" top="50%" transform="translateY(-50%)" ml="1">
              <Box opacity={0} _groupHover={{ opacity: 1 }}>
                <ItemActionsMenu
                  label="Option actions"
                  isVisible
                  canMoveUp={canMoveUp}
                  canMoveDown={canMoveDown}
                  onMoveUp={onMoveUp}
                  onMoveDown={onMoveDown}
                  onDelete={() => removeOption(option.id)}
                  deleteTestId={blockNodeTestIds.deleteOptionButton}
                />
              </Box>
            </Box>
          </Box>
        </HStack>
      </BlockConnectMenu>
    </Reorder.Item>
  );
}
